import type React from "react";

import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { UseQueryResult } from "react-query";
import type { TooltipProps } from "recharts";
import {
  CartesianGrid,
  Label,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent.d";
import type { Props as LabelProps } from "recharts/types/component/Label.d";
import type { AxisDomainItem, CartesianViewBox, ViewBox } from "recharts/types/util/types.d";
import { useTheme } from "styled-components";

import type { FunctionalityHistory } from "api/models/FunctionalityHistory";
import type { Reports } from "api/models/Reports";
import { Notification } from "shared/atoms/Notification";
import { Spinner } from "shared/atoms/Spinner";

import { ChartWrapper, Container, Content, Empty, GoalRect, GoalText, Tooltip as StyledTooltip } from "./helpers";
import { type ChartType, useChartData } from "./useChartData";

interface Props {
  type: ChartType;
  goal: number | null | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  feelingResponse?: UseQueryResult<any[], Error>;
  painResponse?: UseQueryResult<Reports[], Error>;
  physicalFunctionResponse?: UseQueryResult<FunctionalityHistory, Error>;
}

const toNumber = (val: string | number): number => (typeof val === "string" ? parseInt(val, 10) : val);

export const Chart: React.FC<Props> = ({ type, goal, feelingResponse, painResponse, physicalFunctionResponse }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { graphData, isError, isLoading, patient } = useChartData({
    type,
    feelingResponse,
    painResponse,
    physicalFunctionResponse,
  });

  const feelingOrPain = type === "feeling" || type === "pain";
  const goalLineColor = theme.colors.graph.goal;
  const isGoalDefined = goal !== undefined && goal !== null;

  const FunctionTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
    if (active && payload && payload.length !== 0) {
      return (
        <StyledTooltip>
          <Typography variant="subtitle1" sx={{ color: theme.colors.redesign.db60 }}>
            {t("common.week_num", { number: label })}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: theme.colors.redesign.db60 }}>{`${t(
            "patients.physical_function"
          )}: ${payload[0].value}`}</Typography>
        </StyledTooltip>
      );
    }
    return null;
  };

  const PainTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
    if (active && payload && payload.length !== 0) {
      return (
        <StyledTooltip>
          <Typography variant="subtitle1" sx={{ color: theme.colors.redesign.db60 }}>
            {t("common.week_num", { number: label })}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: theme.colors.redesign.db60 }}>
            {`${t("patients.graph.pain_in_joint")}: ${payload[0].value}`}
          </Typography>
        </StyledTooltip>
      );
    }
    return null;
  };

  const FeelingTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
    if (active && payload && payload.length !== 0) {
      return (
        <StyledTooltip>
          <Typography variant="subtitle1" sx={{ color: theme.colors.redesign.db60 }}>
            {t("common.week_num", { number: label })}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: theme.colors.redesign.db60 }}>{`${t(
            "patients.graph.general_feeling"
          )}: ${payload[0].value}`}</Typography>
        </StyledTooltip>
      );
    }
    return null;
  };

  const getTooltipContent = (tooltipType: ChartType) => {
    if (tooltipType === "pain") return <PainTooltip />;
    if (tooltipType === "feeling") return <FeelingTooltip />;
    return <FunctionTooltip />;
  };

  const isCartesianViewbox = (value: ViewBox): value is Required<CartesianViewBox> => {
    return (value as CartesianViewBox).x !== undefined;
  };

  const GoalIndicator = ({ value, viewBox }: LabelProps) => {
    let width: number | string | undefined;

    if (value !== undefined) {
      width = toNumber(value) < 10 ? "14" : "26";
    }

    if (value && viewBox && isCartesianViewbox(viewBox) && width !== undefined) {
      return (
        <>
          <line
            x1={viewBox.x}
            y1={viewBox.y}
            x2={viewBox.x - 10}
            y2={viewBox.y}
            stroke={goalLineColor}
            strokeWidth={4}
          />
          <GoalRect x={viewBox.x - (toNumber(value) < 10 ? 20 : 30)} y={viewBox.y - 11} width={width} />
          <GoalText data-testid="goal-line" x={viewBox.x - 8} y={viewBox.y + 5}>
            {value}
          </GoalText>
        </>
      );
    }
    return null;
  };

  const calculatePainYDomain = () => {
    return [0, 10];
  };

  const calculatePhysicalFunctionYDomain = () => {
    const yMin = (dataMin: number) => {
      if (isGoalDefined && Math.min(goal - 2, dataMin - 2) > 0) {
        const minimum = Math.min(goal - 2, dataMin - 2);
        return minimum % 2 === 0 ? minimum : minimum - 1;
      }
      if (goal === null && dataMin !== Infinity && dataMin - 2 > 0) {
        const minimum = dataMin - 2;
        return minimum % 2 === 0 ? minimum : minimum - 1;
      }
      return 0;
    };
    const yMax = (dataMax: number) => {
      if (graphData.length > 0 || goal !== null || goal !== undefined) {
        if (isGoalDefined && Math.max(goal + 2, dataMax + 2) < 35) {
          const maximum = Math.max(goal + 2, dataMax + 2);
          return maximum % 2 === 0 ? maximum : maximum + 1;
        }
        if (goal === null && dataMax + 2 < 35) {
          const maximum = dataMax + 2;
          return maximum % 2 === 0 ? maximum : maximum + 1;
        }
        return 35;
      }
      return 2;
    };
    return [yMin, yMax];
  };

  const calculateFeelingYDomain = () => {
    return [0, 100];
  };

  const yDomain: AxisDomainItem = () => {
    switch (type) {
      case "feeling":
        return calculateFeelingYDomain();
      case "pain":
        return calculatePainYDomain();
      default:
        return calculatePhysicalFunctionYDomain();
    }
  };

  const getDateKey = () => {
    switch (type) {
      case "feeling":
        return "general_feeling";
      case "pain":
        return "specific_joint_pain";
      default:
        return "repetitions";
    }
  };

  return (
    <Container data-testid="chart">
      <ChartWrapper>
        <Content>
          {isError || isLoading ? (
            <Empty>
              {isLoading ? (
                <Spinner small />
              ) : (
                <Notification variant="danger" style={{ marginBottom: "5px" }}>
                  {t("errors.generic")}
                </Notification>
              )}
            </Empty>
          ) : (
            <ResponsiveContainer width="100%" height={278}>
              <LineChart data={graphData} margin={{ top: 10, right: 15, bottom: 5, left: -27 }}>
                <CartesianGrid stroke={theme.colors.redesign.db20} vertical={false} />
                <XAxis
                  allowDecimals={false}
                  axisLine={false}
                  dataKey="week"
                  tick={{ stroke: theme.colors.redesign.db70, strokeWidth: 0.5, fontSize: "12px" }}
                  tickCount={10}
                  tickLine={false}
                  domain={[
                    0,
                    (dataMax: number) => {
                      if (patient?.week !== undefined && patient?.week >= dataMax) {
                        if (patient?.week !== 0) return patient?.week;
                        return 1;
                      }
                      return dataMax;
                    },
                  ]}
                  type="number"
                />
                <YAxis
                  type="number"
                  domain={yDomain()}
                  axisLine={false}
                  allowDecimals={false}
                  tickLine={false}
                  tickCount={type === "pain" ? 20 : 10}
                  tick={{ stroke: theme.colors.redesign.db100, strokeWidth: 0.5, fontSize: "12px" }}
                  interval={0}
                />
                {isGoalDefined && (
                  <ReferenceLine x="goal" y={goal} stroke={goalLineColor} strokeWidth={4}>
                    <Label value={goal} position="left" content={<GoalIndicator />} />
                  </ReferenceLine>
                )}
                <Line
                  type="monotone"
                  dataKey={getDateKey()}
                  dot={{
                    fill: feelingOrPain ? theme.colors.graph.pain : theme.colors.graph.function,
                    strokeWidth: 3,
                  }}
                  stroke={feelingOrPain ? theme.colors.graph.pain : theme.colors.graph.function}
                  strokeWidth={4}
                  isAnimationActive={false}
                />
                <Tooltip cursor={false} content={getTooltipContent(type)} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Content>
      </ChartWrapper>
    </Container>
  );
};
